/**
 * Canonical list of organizational levels (P1–P6, Sec 1–4) used for level-gated
 * visibility, user assignment, and question metadata. Single source of truth for
 * server models and validation. Update when adding/removing or renaming levels.
 */

export const ORG_LEVELS = Object.freeze([
  'primary_1',
  'primary_2',
  'primary_3',
  'primary_4',
  'primary_5',
  'primary_6',
  'secondary_1',
  'secondary_2',
  'secondary_3',
  'secondary_4',
] as const);

export type OrgLevel = (typeof ORG_LEVELS)[number];

/**
 * Compact human label per level, e.g. `secondary_2` → `"Sec 2"`.
 *
 * Lives here rather than in `src/utils/levelMapping.ts` because the server also
 * writes these strings into human-facing text now — Google Calendar event
 * titles follow the org's manual naming convention (`Sec 2 Math (G1) - Xuan`),
 * and a second copy of the map on the server would let a calendar say "Sec 2"
 * while the app said something else. `levelMapping.ts` derives its own
 * bidirectional tables from this object.
 */
export const ORG_LEVEL_DISPLAY_LABELS: Readonly<Record<OrgLevel, string>> =
  Object.freeze({
    primary_1: 'P1',
    primary_2: 'P2',
    primary_3: 'P3',
    primary_4: 'P4',
    primary_5: 'P5',
    primary_6: 'P6',
    secondary_1: 'Sec 1',
    secondary_2: 'Sec 2',
    secondary_3: 'Sec 3',
    secondary_4: 'Sec 4',
  });

/** `"Sec 2"` for a known org level, `null` for anything else. */
export function orgLevelDisplayLabel(
  orgLevel: string | null | undefined
): string | null {
  if (!orgLevel) return null;
  return ORG_LEVEL_DISPLAY_LABELS[orgLevel as OrgLevel] ?? null;
}
